import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Lock, Unlock, Plus, Trash2, Eye, EyeOff, Shield, 
  Key, RefreshCw, Loader2, AlertTriangle, CheckCircle,
  Youtube, Instagram, Music2, Tv, ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '@/lib/api';
import { AccountCover, AccountStatusBadge } from '@/components/AccountCover';
import { DownloadAccountManager } from '@/components/DownloadAccountManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const platformIcons = {
  youtube: Youtube,
  instagram: Instagram,
  tiktok: Music2,
  bilibili: Tv
};

const platformColors = {
  youtube: 'text-red-500',
  instagram: 'text-pink-500',
  tiktok: 'text-black',
  bilibili: 'text-blue-400'
};

export default function SecureVault() {
  const { user } = useAuth();
  
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [masterPassword, setMasterPassword] = useState('');
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [revealedCredential, setRevealedCredential] = useState(null);
  const [transferFor, setTransferFor] = useState(null);
  const [transferPlatform, setTransferPlatform] = useState('');
  const [transferring, setTransferring] = useState(false);
  
  // New credential form
  const [newCredential, setNewCredential] = useState({
    platform: 'youtube',
    username: '',
    password: '',
    additional: {}
  });

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await api.listVaultCredentials();
      setCredentials(res.data.credentials || []);
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    }
  }, []);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleUnlockVault = async () => {
    if (!masterPassword.trim()) {
      toast.error('Digite a senha mestra');
      return;
    }
    
    // For now, just set unlocked - actual verification happens on retrieve
    setVaultUnlocked(true);
    toast.success('Cofre desbloqueado');
  };

  const handleLockVault = () => {
    setVaultUnlocked(false);
    setMasterPassword('');
    setRevealedCredential(null);
    toast.info('Cofre bloqueado');
  };

  const handleAddCredential = async (e) => {
    e.preventDefault();
    
    if (!newCredential.username || !newCredential.password) {
      toast.error('Preencha usuário e senha');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.storeVaultCredential({
        master_password: masterPassword,
        platform: newCredential.platform,
        username: newCredential.username,
        password: newCredential.password,
        additional: newCredential.additional,
        account_label: newCredential.account_label || newCredential.username
      });
      
      toast.success('Credencial armazenada com criptografia');
      setNewCredential({ platform: 'youtube', username: '', password: '', additional: {}, account_label: '' });
      setShowAddForm(false);
      fetchCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao armazenar credencial');
    } finally {
      setLoading(false);
    }
  };

  const handleRevealCredential = async (credentialId) => {
    setLoading(true);
    
    try {
      const res = await api.retrieveVaultCredential({
        master_password: masterPassword,
        credential_id: credentialId
      });
      
      setRevealedCredential(res.data);
      toast.success('Credencial revelada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senha mestra incorreta');
    } finally {
      setLoading(false);
    }
  };

  const openTransfer = (cred) => {
    setTransferFor(cred);
    const options = ['youtube', 'tiktok', 'instagram', 'bilibili'].filter(p => p !== cred.platform);
    setTransferPlatform(options[0]);
  };

  const handleTransferCredential = async () => {
    if (!transferFor || !transferPlatform) return;
    if (!masterPassword) {
      toast.error('Desbloqueie o cofre com a senha mestra');
      return;
    }
    setTransferring(true);
    try {
      await api.transferVaultCredentialPlatform(transferFor.id, {
        master_password: masterPassword,
        target_platform: transferPlatform,
        account_label: transferFor.account_label || undefined
      });
      toast.success(`Credencial transferida para ${transferPlatform}`);
      setTransferFor(null);
      setTransferPlatform('');
      fetchCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir credencial');
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta credencial permanentemente?')) {
      return;
    }
    
    try {
      await api.deleteVaultCredential(credentialId, masterPassword);
      toast.success('Credencial deletada com segurança');
      fetchCredentials();
      if (revealedCredential?.credential_id === credentialId) {
        setRevealedCredential(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar');
    }
  };

  const PlatformIcon = ({ platform }) => {
    const Icon = platformIcons[platform] || Key;
    const color = platformColors[platform] || 'text-gray-500';
    return <Icon className={`w-5 h-5 ${color}`} />;
  };

  const socialCreds = credentials.filter((c) => c.platform !== 'download_account');
  const downloadCreds = credentials.filter((c) => c.platform === 'download_account');

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Cofre Seguro
          </h1>
          <p className="text-muted-foreground">
            Armazene credenciais com criptografia de ponta a ponta
          </p>
        </div>
        
        {vaultUnlocked && (
          <Button variant="outline" onClick={handleLockVault}>
            <Lock className="w-4 h-4 mr-2" />
            Bloquear
          </Button>
        )}
      </div>

      {/* Vault Lock Screen */}
      {!vaultUnlocked ? (
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle>Cofre Bloqueado</CardTitle>
            <CardDescription>
              Digite sua senha mestra para acessar as credenciais
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => { e.preventDefault(); handleUnlockVault(); }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="masterPassword">Senha Mestra</Label>
                <Input
                  id="masterPassword"
                  type="password"
                  placeholder="Digite sua senha mestra"
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                  className="text-center"
                />
              </div>
              <Button type="submit" className="w-full">
                <Unlock className="w-4 h-4 mr-2" />
                Desbloquear Cofre
              </Button>
            </form>
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 text-green-500" />
                <div>
                  <p className="font-medium text-foreground">Criptografia AES-256-GCM</p>
                  <p>Suas credenciais são criptografadas localmente antes de serem armazenadas.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Security Status */}
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="flex items-center gap-4 py-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="font-medium text-green-700 dark:text-green-400">Cofre Desbloqueado</p>
                <p className="text-sm text-muted-foreground">
                  {credentials.length} credencial(is) armazenada(s) com segurança
                </p>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="social" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="social" data-testid="vault-tab-social">Redes Sociais</TabsTrigger>
              <TabsTrigger value="download" data-testid="vault-tab-download">Conta de Download</TabsTrigger>
            </TabsList>

            <TabsContent value="social" className="space-y-4 mt-4">
          {/* Add Credential Form */}
          {showAddForm ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Nova Credencial</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddCredential} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Plataforma</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {['youtube', 'tiktok', 'instagram', 'bilibili'].map((platform) => (
                        <Button
                          key={platform}
                          type="button"
                          variant={newCredential.platform === platform ? "default" : "outline"}
                          className="flex flex-col items-center gap-1 h-auto py-3"
                          onClick={() => setNewCredential(prev => ({ ...prev, platform }))}
                        >
                          <PlatformIcon platform={platform} />
                          <span className="text-xs capitalize">{platform}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="username">Usuário / Email</Label>
                    <Input
                      id="username"
                      placeholder="seu_usuario ou email@exemplo.com"
                      value={newCredential.username}
                      onChange={(e) => setNewCredential(prev => ({ ...prev, username: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="account_label">Nome da conta (exibição)</Label>
                    <Input
                      id="account_label"
                      placeholder="Ex.: Canal Principal, @minhaconta"
                      value={newCredential.account_label || ''}
                      onChange={(e) => setNewCredential(prev => ({ ...prev, account_label: e.target.value }))}
                      data-testid="vault-account-label-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Senha</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Senha da conta"
                      value={newCredential.password}
                      onChange={(e) => setNewCredential(prev => ({ ...prev, password: e.target.value }))}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      <span className="ml-2">Salvar Credencial</span>
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Credencial
            </Button>
          )}

          {/* Credentials List */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Credenciais Armazenadas</h2>
            
            {socialCreds.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Key className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nenhuma credencial armazenada</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {socialCreds.map((cred) => (
                  <Card key={cred.id}>
                    <CardContent className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <PlatformIcon platform={cred.platform} />
                        </div>
                        <div>
                          <p className="font-medium">{cred.account_label || cred.platform}</p>
                          <p className="text-sm text-muted-foreground capitalize">
                            {cred.platform} · {cred.username_hint}
                          </p>
                          <div className="mt-1"><AccountStatusBadge status={cred.status} /></div>
                        </div>
                        <div className="hidden md:block pl-4 border-l border-border">
                          <AccountCover credential={cred} onChanged={fetchCredentials} />
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          Criptografado
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          title="Pedir ao Agente para verificar a conta"
                          onClick={() => api.requestAccountCheck(cred.id).then(r => toast.success(r.data.message)).catch(() => toast.error('Falha ao solicitar verificação'))}
                          data-testid={`vault-check-account-${cred.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleRevealCredential(cred.id)}
                          disabled={loading}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          title="Transferir para outra plataforma"
                          onClick={() => (transferFor?.id === cred.id ? setTransferFor(null) : openTransfer(cred))}
                          data-testid={`vault-transfer-${cred.id}`}
                        >
                          <ArrowLeftRight className="w-4 h-4" />
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteCredential(cred.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                    
                    {/* Revealed Credential */}
                    {revealedCredential?.credential_id === cred.id && (
                      <CardContent className="border-t bg-muted/30 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Usuário:</span>
                          <code className="text-sm">{revealedCredential.username}</code>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Senha:</span>
                          <code className="text-sm">{revealedCredential.password}</code>
                        </div>
                        {revealedCredential.additional && Object.keys(revealedCredential.additional).length > 0 && (
                          <div className="pt-2 border-t">
                            <span className="text-sm text-muted-foreground">Adicional:</span>
                            <pre className="text-xs mt-1 p-2 bg-background rounded">
                              {JSON.stringify(revealedCredential.additional, null, 2)}
                            </pre>
                          </div>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="w-full mt-2"
                          onClick={() => setRevealedCredential(null)}
                        >
                          <EyeOff className="w-4 h-4 mr-2" />
                          Ocultar
                        </Button>
                      </CardContent>
                    )}

                    {/* Transfer to another platform */}
                    {transferFor?.id === cred.id && (
                      <CardContent className="border-t bg-muted/30 space-y-3" data-testid={`vault-transfer-panel-${cred.id}`}>
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4 text-primary" />
                          <p className="text-sm font-medium">Transferir para outra plataforma</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Cria uma cópia desta credencial na plataforma escolhida (a original é mantida).
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {['youtube', 'tiktok', 'instagram', 'bilibili']
                            .filter((p) => p !== cred.platform)
                            .map((platform) => (
                              <Button
                                key={platform}
                                type="button"
                                variant={transferPlatform === platform ? 'default' : 'outline'}
                                className="flex flex-col items-center gap-1 h-auto py-2"
                                onClick={() => setTransferPlatform(platform)}
                                data-testid={`vault-transfer-target-${platform}`}
                              >
                                <PlatformIcon platform={platform} />
                                <span className="text-xs capitalize">{platform}</span>
                              </Button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleTransferCredential}
                            disabled={transferring || !transferPlatform}
                            data-testid={`vault-transfer-confirm-${cred.id}`}
                          >
                            {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
                            <span className="ml-2">Transferir</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setTransferFor(null)}>
                            Cancelar
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
            </TabsContent>

            <TabsContent value="download" className="mt-4">
              <DownloadAccountManager
                masterPassword={masterPassword}
                credentials={downloadCreds}
                onChanged={fetchCredentials}
              />
            </TabsContent>
          </Tabs>

          {/* E2E Protocol Info */}
          <Card className="bg-muted/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-500" />
                Protocolo de Segurança E2E
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <p>Criptografia AES-256-GCM de ponta a ponta</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <p>Derivação de chave PBKDF2 com 480.000 iterações</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <p>Dupla criptografia para transferência App ↔ CUA</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                <p>Credenciais nunca transmitidas em texto plano</p>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                <p className="text-yellow-600 dark:text-yellow-400">
                  Se você perder a senha mestra, não será possível recuperar as credenciais.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
